import React, { useEffect, useMemo, useState } from 'react';
import { Search, Loader2, RefreshCw, Trash2, Download, ChevronDown, Gift } from 'lucide-react';
import api from '../../utils/api';
import PackageSearchSelect from './PackageSearchSelect';

const EXPORT_SEGMENT_SIZE = 50000;

const formatWan = (count) => {
  const wan = count / 10000;
  return Number.isInteger(wan) ? String(wan) : String(Math.round(wan * 10) / 10);
};

export default function UsersTab() {
  const [loading, setLoading] = useState(true);
  const [packagesLoading, setPackagesLoading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [keyword, setKeyword] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [downloadSource, setDownloadSource] = useState('');
  const [selectedLabel, setSelectedLabel] = useState('');
  const [selectedMemberStatus, setSelectedMemberStatus] = useState('');
  const [sourceOptions, setSourceOptions] = useState([]);
  const [labelOptions, setLabelOptions] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 1 });
  const [jumpPage, setJumpPage] = useState('');
  const [users, setUsers] = useState([]);
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [grantMenu, setGrantMenu] = useState(null); // 赠送会员下拉：{ user, top, bottom, right }
  const [grantPlans, setGrantPlans] = useState([]);
  const [grantPlansLoading, setGrantPlansLoading] = useState(false);
  const [grantingId, setGrantingId] = useState(null);

  const fetchUsers = async (page = 1, kw = keyword, source = downloadSource, label = selectedLabel, memberStatus = selectedMemberStatus) => {
    setLoading(true);
    setExportMenuOpen(false);
    try {
      const res = await api.get('/admin/users', {
        params: {
          page,
          limit: pagination.limit,
          keyword: kw || undefined,
          downloadSource: source || undefined,
          userLabel: label || undefined,
          memberStatus: memberStatus || undefined
        }
      });
      if (res.data.success) {
        const data = res.data.data || res.data;
        setUsers(data.list || []);
        setLabelOptions(Array.isArray(data.labelOptions) ? data.labelOptions : []);
        setPagination({
          page: data.page,
          limit: data.limit,
          total: data.total,
          totalPages: data.totalPages
        });
      }
    } catch (e) {
      console.error('获取用户列表失败:', e);
    } finally {
      setLoading(false);
    }
  };

  // 导出区间菜单：按当前总数分段，主按钮默认导出最新5万条，菜单项为后续每 5 万一段
  const exportMenuSegments = useMemo(() => {
    const total = pagination.total;
    if (!total || total <= EXPORT_SEGMENT_SIZE) return [];
    const segCount = Math.ceil(total / EXPORT_SEGMENT_SIZE) - 1;
    const segments = [];
    for (let i = 1; i <= segCount; i++) {
      const startCount = i * EXPORT_SEGMENT_SIZE;
      const endCount = Math.min((i + 1) * EXPORT_SEGMENT_SIZE, total);
      segments.push({ offset: startCount, label: `${formatWan(startCount)}万-${formatWan(endCount)}万条` });
    }
    return segments;
  }, [pagination.total]);

  const handleExportRange = async (segment) => {
    setExportMenuOpen(false);
    setExporting(true);
    try {
      const response = await api.get('/admin/users/export', {
        params: {
          keyword: keyword || undefined,
          downloadSource: downloadSource || undefined,
          userLabel: selectedLabel || undefined,
          memberStatus: selectedMemberStatus || undefined,
          offset: segment.offset,
          limit: EXPORT_SEGMENT_SIZE
        },
        responseType: 'blob',
        timeout: 0
      });

      const contentType = response.headers?.['content-type'] || '';
      if (contentType.includes('application/json')) {
        const text = await new Response(response.data).text();
        let message = '导出失败';
        try {
          const parsed = JSON.parse(text);
          message = parsed?.error || parsed?.message || message;
        } catch (e) { /* ignore */ }
        alert(message);
        return;
      }

      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `用户列表_${segment.label}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('导出用户列表失败:', error);
      alert('导出失败，请稍后重试');
    } finally {
      setExporting(false);
    }
  };

  const fetchSourceOptions = async () => {
    setPackagesLoading(true);
    try {
      const res = await api.get('/admin/download-package-names');
      if (res.data.success) {
        const data = res.data.data || res.data || {};
        const labels = Array.from(
          new Set((data.list || []).map((item) => String(item?.label || '').trim()).filter(Boolean))
        );
        setSourceOptions([
          ...labels.map((label, index) => ({
            id: `label-${index}`,
            value: label,
            label
          })),
          {
            id: 'unknown-user',
            value: '__unknown__',
            label: '未知用户'
          },
          {
            id: 'abnormal-client',
            value: '__abnormal__',
            label: '客户端异常登录'
          }
        ]);
      }
    } catch (e) {
      console.error('获取下载来源失败:', e);
    } finally {
      setPackagesLoading(false);
    }
  };

  useEffect(() => {
    fetchSourceOptions();
    fetchUsers(1, keyword, downloadSource, selectedLabel);
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    setKeyword(searchInput);
    fetchUsers(1, searchInput, downloadSource);
  };

  const handleReset = () => {
    setSearchInput('');
    setKeyword('');
    setDownloadSource('');
    setSelectedLabel('');
    setSelectedMemberStatus('');
    setJumpPage('');
    fetchUsers(1, '', '', '', '');
  };

  const handleJump = () => {
    const page = Number(jumpPage);
    if (!jumpPage.trim() || isNaN(page)) {
      setJumpPage('');
      return;
    }
    if (page < 1 || page > pagination.totalPages) {
      alert(`请输入 1 ~ ${pagination.totalPages} 之间的页码`);
      return;
    }
    fetchUsers(page, keyword, downloadSource, selectedLabel);
    setJumpPage('');
  };

  const handleSourceChange = (value) => {
    setDownloadSource(value);
    fetchUsers(1, keyword, value, selectedLabel);
  };

  const handleLabelChange = (value) => {
    setSelectedLabel(value);
    fetchUsers(1, keyword, downloadSource, value);
  };

  const handleMemberStatusChange = (value) => {
    setSelectedMemberStatus(value);
    fetchUsers(1, keyword, downloadSource, selectedLabel, value);
  };

  const deleteTestAccount = async (user) => {
    if (!String(user?.phone || '').startsWith('1876711')) return;
    const confirmedPhone = window.prompt(`请输入手机号 ${user.phone} 确认删除测试账号`);
    if (confirmedPhone !== user.phone) {
      alert('手机号确认不一致，已取消删除');
      return;
    }

    setDeletingId(user.id);
    try {
      const res = await api.delete(`/admin/users/${user.id}/test-account`);
      if (res.data.success) {
        alert('测试账号已删除');
        fetchUsers(1, keyword, downloadSource, selectedLabel);
      }
    } catch (e) {
      alert(e.response?.data?.error || '删除失败');
    } finally {
      setDeletingId(null);
    }
  };

  // 赠送会员下拉：打开时动态读取全部上架套餐（运营改库自动同步）
  const openGrantMenu = async (user, event) => {
    if (grantMenu?.user?.id === user.id) {
      setGrantMenu(null);
      return;
    }
    const rect = event.currentTarget.getBoundingClientRect();
    const estimatedHeight = 280; // 菜单最大高度估计值，用于决定向上或向下弹出
    const openUp = rect.bottom + estimatedHeight > window.innerHeight && rect.top > estimatedHeight;
    setGrantMenu({
      user,
      top: openUp ? null : rect.bottom + 4,
      bottom: openUp ? window.innerHeight - rect.top + 4 : null,
      right: Math.max(8, window.innerWidth - rect.right)
    });

    setGrantPlansLoading(true);
    try {
      const res = await api.get('/admin/membership-plans/options');
      if (res.data.success) {
        const data = res.data.data || res.data || {};
        setGrantPlans(Array.isArray(data.plans) ? data.plans : []);
      }
    } catch (e) {
      console.error('获取可赠送套餐失败:', e);
    } finally {
      setGrantPlansLoading(false);
    }
  };

  // 选项点击 → 二次确认 → 调接口赠送（不生成订单、不发积分、不动免费次数）
  const handleGrantPlan = async (user, plan) => {
    setGrantMenu(null);
    const userText = user.phone || user.nickname || user.username || `ID ${user.id}`;
    const durationText = plan.is_lifetime ? '终身会员' : `${plan.duration_days} 天`;
    const currentText = user.membership_status === 'lifetime'
      ? '当前已是终身会员'
      : (user.zip_membership_expire_at ? `当前会员到期：${user.zip_membership_expire_at}` : '当前无有效会员');
    const effectText = plan.is_lifetime
      ? '赠送后该用户将获得终身会员（到期时间 2099-12-31）。'
      : `赠送后将在现有到期时间基础上顺延 ${plan.duration_days} 天。`;
    const confirmed = window.confirm(
      `确认给用户 ${userText} 赠送「${plan.name}」（${durationText}）？\n\n${currentText}\n${effectText}\n不生成订单、不发积分、不影响免费次数。`
    );
    if (!confirmed) return;

    setGrantingId(user.id);
    try {
      const res = await api.post(`/admin/users/${user.id}/grant-membership`, { planId: plan.id });
      if (res.data.success) {
        alert(`赠送成功\n新会员到期时间：${res.data.afterExpireAt || '-'}`);
        fetchUsers(pagination.page, keyword, downloadSource, selectedLabel, selectedMemberStatus);
      }
    } catch (e) {
      alert(e.response?.data?.error || e.message || '赠送失败');
    } finally {
      setGrantingId(null);
    }
  };

  // 下拉打开时滚动/缩放窗口自动关闭，避免悬浮菜单位置错乱
  useEffect(() => {
    if (!grantMenu) return undefined;
    const closeMenu = () => setGrantMenu(null);
    window.addEventListener('scroll', closeMenu, true);
    window.addEventListener('resize', closeMenu);
    return () => {
      window.removeEventListener('scroll', closeMenu, true);
      window.removeEventListener('resize', closeMenu);
    };
  }, [grantMenu]);

  const getDownloadSourceText = (user) => {
    if (user?.download_source_label) {
      return user.download_source_label;
    }

    if (user?.first_installer_name) {
      return '未知用户';
    }

    return '客户端异常登录';
  };

  const renderUserLabels = (labels) => {
    const normalizedLabels = Array.isArray(labels) ? labels.filter(Boolean) : [];
    if (normalizedLabels.length <= 0) {
      return <span className="text-sm text-gray-400">-</span>;
    }

    return (
      <div className="flex flex-wrap gap-2">
        {normalizedLabels.map((label) => (
          <span
            key={label}
            className="inline-flex items-center rounded-full bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-700 ring-1 ring-inset ring-amber-200"
          >
            {label}
          </span>
        ))}
      </div>
    );
  };

  const renderMembership = (user) => {
    const status = user?.membership_status;
    if (status === 'lifetime') {
      return (
        <span className="inline-flex items-center rounded-full bg-amber-100 px-2.5 py-1 text-xs font-medium text-amber-700 ring-1 ring-inset ring-amber-200">
          终身会员
        </span>
      );
    }
    if (status === 'active') {
      return (
        <span className="inline-flex items-center rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-700 ring-1 ring-inset ring-emerald-200">
          会员
        </span>
      );
    }
    if (status === 'expired') {
      return (
        <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-500 ring-1 ring-inset ring-gray-200">
          已过期
        </span>
      );
    }
    return <span className="text-sm text-gray-400">-</span>;
  };

  const renderMembershipExpireAt = (user) => {
    const status = user?.membership_status;
    const expireAt = user?.zip_membership_expire_at ? String(user.zip_membership_expire_at) : '';
    // 终身会员也展示具体到期时间（2099-12-31），仅用颜色区分
    if ((status === 'lifetime' || status === 'active' || status === 'expired') && expireAt) {
      const colorCls = status === 'lifetime' ? 'font-medium text-amber-700' : (status === 'expired' ? 'text-gray-400' : 'text-gray-900');
      return <span className={`text-sm ${colorCls}`}>{expireAt}</span>;
    }
    return <span className="text-sm text-gray-400">-</span>;
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white">用户列表</h2>
        <form onSubmit={handleSearch} className="flex items-center gap-2 w-full sm:w-auto">
          <div className="relative flex-1 sm:flex-none">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="UID/手机号/设备号/用户名"
              className="pl-10 pr-4 py-2 w-full sm:w-64 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <PackageSearchSelect
            value={downloadSource}
            onChange={handleSourceChange}
            options={sourceOptions}
            loading={packagesLoading}
            placeholder="全部来源"
            className="w-full sm:w-64 min-w-0"
            getOptionValue={(item) => item.value}
            getOptionDisplayName={(item) => item.label}
            searchPlaceholder="搜索渠道来源"
          />
          <PackageSearchSelect
            value={selectedLabel}
            onChange={handleLabelChange}
            options={labelOptions.map((label, index) => ({
              id: `user-label-${index}`,
              value: label,
              label
            }))}
            placeholder="全部标签"
            className="w-full sm:w-52 min-w-0"
            getOptionValue={(item) => item.value}
            getOptionDisplayName={(item) => item.label}
            searchPlaceholder="搜索用户标签"
          />
          <select
            value={selectedMemberStatus}
            onChange={(e) => handleMemberStatusChange(e.target.value)}
            className="px-3 py-2 w-full sm:w-36 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
          >
            <option value="">全部会员</option>
            <option value="active">有效会员</option>
            <option value="lifetime">终身会员</option>
            <option value="expired">已过期</option>
            <option value="none">从未开通</option>
          </select>
          <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
            搜索
          </button>
          <button type="button" onClick={handleReset} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200">
            重置
          </button>
          <div className="relative">
            <div className="flex overflow-hidden rounded-lg border border-emerald-200">
              <button
                type="button"
                onClick={() => handleExportRange({ offset: 0, label: '最新5万条' })}
                disabled={exporting || loading || pagination.total === 0}
                className="inline-flex items-center gap-2 bg-emerald-50 px-4 py-2 text-sm text-emerald-700 hover:bg-emerald-100 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-400"
              >
                <Download className="h-4 w-4" />
                {exporting ? '导出中…' : '导出最新5万条'}
              </button>
              <button
                type="button"
                onClick={() => setExportMenuOpen((open) => !open)}
                disabled={exporting || loading || exportMenuSegments.length === 0}
                className="border-l border-emerald-200 bg-emerald-50 px-2 py-2 text-emerald-700 hover:bg-emerald-100 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-400"
                title="选择导出区间"
              >
                <ChevronDown className="h-4 w-4" />
              </button>
            </div>
            {exportMenuOpen && exportMenuSegments.length > 0 ? (
              <div className="absolute right-0 top-full z-50 mt-1 max-h-72 w-44 overflow-y-auto rounded-lg border border-gray-200 bg-white py-1 shadow-lg dark:border-gray-700 dark:bg-gray-800">
                {exportMenuSegments.map((seg) => (
                  <button
                    key={seg.offset}
                    type="button"
                    onClick={() => handleExportRange(seg)}
                    className="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-700"
                  >
                    {seg.label}
                  </button>
                ))}
              </div>
            ) : null}
          </div>
        </form>
        {exportMenuOpen ? (
          <div className="fixed inset-0 z-40" onClick={() => setExportMenuOpen(false)} />
        ) : null}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      ) : (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">手机号</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">标签</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">会员</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">会员到期时间</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">下载来源</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">注册时间</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">免费次数</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">操作</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {users.map((u) => {
                  return (
                  <tr key={u.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{u.id}</td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {u.phone ? (
                        <span className="whitespace-nowrap">{u.phone}</span>
                      ) : (
                        <div>
                          <div className="flex items-center gap-1.5 whitespace-nowrap">
                            <span className="inline-flex items-center rounded-full bg-blue-50 px-2 py-0.5 text-xs text-blue-600">设备</span>
                            <span>{u.nickname || '设备用户'}</span>
                          </div>
                          <div className="mt-1 text-xs text-gray-400 break-all" title={u.username || undefined}>{u.username || '-'}</div>
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">{renderUserLabels(u.user_labels)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">{renderMembership(u)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">{renderMembershipExpireAt(u)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" title={u.first_installer_name || undefined}>
                      {getDownloadSourceText(u)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{u.created_at ? String(u.created_at) : '-'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{u.free_uses_remaining ?? 0}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                      <div className="inline-flex items-center gap-2">
                        <button
                          disabled={grantingId === u.id}
                          onClick={(e) => openGrantMenu(u, e)}
                          className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-white ${
                            grantingId === u.id ? 'bg-gray-400' : 'bg-emerald-600 hover:bg-emerald-700'
                          }`}
                        >
                          {grantingId === u.id ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Gift className="w-4 h-4" />}
                          赠送会员
                        </button>
                        {String(u.phone || '').startsWith('1876711') ? (
                          <button
                            disabled={deletingId === u.id}
                            onClick={() => deleteTestAccount(u)}
                            className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-white ${
                              deletingId === u.id ? 'bg-gray-400' : 'bg-red-600 hover:bg-red-700'
                            }`}
                          >
                            {deletingId === u.id ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                            删除测试号
                          </button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="px-6 py-4 flex items-center justify-between bg-gray-50">
            <div className="text-sm text-gray-600">
              共 {pagination.total} 条，第 {pagination.page}/{pagination.totalPages} 页
            </div>
            <div className="flex items-center gap-2">
              <input
                type="number"
                value={jumpPage}
                onChange={(e) => setJumpPage(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleJump()}
                placeholder="页码"
                min={1}
                max={pagination.totalPages}
                className="w-16 rounded-lg border border-gray-300 px-2 py-1 text-sm text-center focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              />
              <button
                onClick={handleJump}
                className="rounded-lg border border-gray-300 px-3 py-1 text-sm hover:bg-gray-100 dark:border-gray-600 dark:hover:bg-gray-700"
              >
                跳转
              </button>
              <button
                disabled={pagination.page <= 1}
                onClick={() => fetchUsers(pagination.page - 1, keyword, downloadSource, selectedLabel)}
                className="px-3 py-1 border border-gray-300 rounded-lg disabled:opacity-50"
              >
                上一页
              </button>
              <button
                disabled={pagination.page >= pagination.totalPages}
                onClick={() => fetchUsers(pagination.page + 1, keyword, downloadSource, selectedLabel)}
                className="px-3 py-1 border border-gray-300 rounded-lg disabled:opacity-50"
              >
                下一页
              </button>
            </div>
          </div>
        </div>
      )}

      {grantMenu ? (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setGrantMenu(null)} />
          <div
            className="fixed z-50 max-h-72 w-56 overflow-y-auto rounded-lg border border-gray-200 bg-white py-1 shadow-lg dark:border-gray-700 dark:bg-gray-800"
            style={{
              top: grantMenu.top != null ? `${grantMenu.top}px` : undefined,
              bottom: grantMenu.bottom != null ? `${grantMenu.bottom}px` : undefined,
              right: `${grantMenu.right}px`
            }}
          >
            <div className="px-3 py-1.5 text-xs text-gray-400">选择要赠送的套餐</div>
            {grantPlansLoading && grantPlans.length === 0 ? (
              <div className="flex items-center justify-center gap-2 px-3 py-3 text-sm text-gray-500">
                <Loader2 className="w-4 h-4 animate-spin" />
                加载中…
              </div>
            ) : grantPlans.length === 0 ? (
              <div className="px-3 py-3 text-sm text-gray-400">暂无可赠送套餐</div>
            ) : (
              grantPlans.map((plan) => (
                <button
                  key={plan.id}
                  type="button"
                  onClick={() => handleGrantPlan(grantMenu.user, plan)}
                  className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm text-gray-700 hover:bg-emerald-50 dark:text-gray-200 dark:hover:bg-gray-700"
                >
                  <span className="truncate">{plan.name}</span>
                  <span className="shrink-0 text-xs text-gray-400">{plan.is_lifetime ? '终身' : `${plan.duration_days}天`}</span>
                </button>
              ))
            )}
          </div>
        </>
      ) : null}
    </div>
  );
}
